import api from './api'
import type { PaginationMeta } from '@/types/api.types'
import type {
  SalesOrder,
  SalesOrderFilters,
  SalesOrderFulfillmentSummary,
} from '@/types/selling.types'

function mapListResponse<T>(r: { data: T[]; total?: number; page: number; per_page: number; total_pages?: number }) {
  return { 
    data: r.data, 
    meta: { 
      current_page: r.page, 
      per_page: r.per_page, 
      total: r.total || r.data.length, 
      last_page: r.total_pages || 1 
    } as PaginationMeta 
  }
}

class SalesOrderService {
  async getSalesOrders(params?: SalesOrderFilters & { page?: number; page_size?: number }): Promise<{ data: SalesOrder[]; meta: PaginationMeta }> {
    const response = await api.get<any>('/api/v1/sales-orders/', { params })
    return mapListResponse(response.data)
  }

  async getSalesOrdersByContract(
    contractId: string,
    params?: { page?: number; page_size?: number; only_with_remaining_to_invoice?: boolean },
  ): Promise<{ data: SalesOrder[]; meta: PaginationMeta }> {
    const response = await api.get<any>(`/api/v1/sales-orders/contract/${contractId}`, { params })
    return mapListResponse(response.data)
  }

  async getSalesOrdersByCustomer(
    customerId: string,
    params?: { page?: number; page_size?: number; only_with_remaining_to_invoice?: boolean },
  ): Promise<{ data: SalesOrder[]; meta: PaginationMeta }> {
    const response = await api.get<any>(`/api/v1/sales-orders/customer/${customerId}`, { params })
    return mapListResponse(response.data)
  }

  async getSalesOrder(id: string): Promise<SalesOrder> {
    const response = await api.get<SalesOrder>(`/api/v1/sales-orders/${id}`)
    return response.data
  }

  async getSalesOrderFulfillment(id: string): Promise<SalesOrderFulfillmentSummary> {
    const response = await api.get<SalesOrderFulfillmentSummary>(
      `/api/v1/sales-orders/${id}/fulfillment`,
    )
    return response.data
  }

  async createSalesOrder(data: any): Promise<SalesOrder> {
    const response = await api.post<SalesOrder>('/api/v1/sales-orders/', data)
    return response.data
  }

  async updateSalesOrder(id: string, data: any): Promise<SalesOrder> {
    const response = await api.put<SalesOrder>(`/api/v1/sales-orders/${id}`, data)
    return response.data
  }

  async updateStatus(id: string, status: number): Promise<SalesOrder> {
    const response = await api.patch<SalesOrder>(`/api/v1/sales-orders/${id}/status`, { status })
    return response.data
  }

  async deleteSalesOrder(id: string): Promise<void> {
    await api.delete(`/api/v1/sales-orders/${id}`)
  }

  async submitSalesOrder(id: string): Promise<SalesOrder> {
    const response = await api.post<SalesOrder>(`/api/v1/sales-orders/${id}/submit`)
    return response.data
  }

  async cancelSalesOrder(id: string): Promise<SalesOrder> {
    const response = await api.post<SalesOrder>(`/api/v1/sales-orders/${id}/cancel`)
    return response.data
  }

  async getSalesOrderItems(id: string): Promise<any[]> {
    const response = await api.get<{ data: any[] }>(`/api/v1/sales-orders/${id}/items`)
    return response.data.data ?? response.data
  }

  async addSalesOrderItem(id: string, data: any): Promise<any> {
    const response = await api.post<any>(`/api/v1/sales-orders/${id}/items`, data)
    return response.data
  }

  async updateSalesOrderItem(orderId: string, itemId: string, data: any): Promise<any> {
    const response = await api.put<any>(`/api/v1/sales-orders/${orderId}/items/${itemId}`, data)
    return response.data
  }

  async deleteSalesOrderItem(orderId: string, itemId: string): Promise<void> {
    await api.delete(`/api/v1/sales-orders/${orderId}/items/${itemId}`)
  }

  async getSalesOrderInvoices(id: string): Promise<any[]> {
    const response = await api.get<{ data: any[] }>(`/api/v1/sales-orders/${id}/invoices`)
    return response.data.data ?? response.data
  }

  async linkInvoiceToSalesOrder(orderId: string, invoiceId: string, amount?: number): Promise<void> {
    await api.post(`/api/v1/sales-orders/${orderId}/invoices`, { invoice_id: invoiceId, amount })
  }

  async unlinkInvoiceFromSalesOrder(orderId: string, invoiceId: string): Promise<void> {
    await api.delete(`/api/v1/sales-orders/${orderId}/invoices/${invoiceId}`)
  }

  async fetchPdfBlob(id: string): Promise<Blob> {
    const response = await api.get(`/api/v1/sales-orders/${id}/pdf`, { responseType: 'blob' })
    return response.data as Blob
  }
}

export default new SalesOrderService()
